import React, {useEffect, useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import debounce from 'lodash/debounce';
import {alpha} from '@mui/material/styles';
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Add,
    Clear,
    EditOutlined,
    Refresh,
    Search,
    SendOutlined,
    TaskAltOutlined, TouchAppOutlined
} from '@mui/icons-material';
import AirbnbDateRangePicker from '../utils/AirbnbDateRangePicker';
import {ddToolbarActions} from '../../store/actions/ddToolbarActions';

const views = {
    clicks: {
        title: 'Clicks',
        description: 'Explore click activity and record conversions.',
        icon: TouchAppOutlined,
        action: 'Add conversion',
        actionIcon: Add,
        route: '/log/clicks/add-conversion/'
    },
    conversions: {
        title: 'Conversions',
        description: 'Review conversions and adjust their details.',
        icon: TaskAltOutlined,
        action: 'Override conversion',
        actionIcon: EditOutlined,
        route: '/log/conversions/override-conversion/'
    },
    postbacks: {
        title: 'Postbacks',
        description: 'Inspect postback requests and responses.',
        icon: SendOutlined
    }
};

export function LogToolbar(
    {
        kind, rows = [], selectedRows = new Set(), searchTerm = '', onChangeSearch,
        onClickRefresh, history, location, ddToolbarState, updateDateRange, children
    }
) {
    const view = views[kind];
    const Icon = view.icon;
    const ActionIcon = view.actionIcon;
    const mobile = useMediaQuery(useTheme().breakpoints.down('sm'));
    const [search, setSearch] = useState(searchTerm);
    const onSearch = useRef(onChangeSearch);
    onSearch.current = onChangeSearch;
    const updateSearch = useMemo(() => debounce(value => onSearch.current(value), 800), []);

    useEffect(() => () => updateSearch.cancel(), [updateSearch]);

    useEffect(() => {
        updateSearch.cancel();
        setSearch(searchTerm);
    }, [searchTerm, updateSearch]);

    const selectedRow = selectedRows.size === 1 ? rows.find(row => selectedRows.has(row.uniqueId)) : null;
    const {from, to, dateLabel} = ddToolbarState;

    const hasAction = !!view.action || !!children;

    return <Box component="section" aria-label={`${view.title} log toolbar`} sx={{
        p: {xs: 1.5, sm: 2, md: 2.5}, flexShrink: 0,
        bgcolor: 'background.paper', minWidth: 0, display: 'grid', alignItems: 'center', gap: {xs: 1.25, sm: 2},
        gridTemplateColumns: {xs: 'minmax(0, 1fr) auto auto', sm: 'minmax(0, 300px) minmax(0, 1fr) minmax(0, max-content) auto'},
        gridTemplateAreas: {
            xs: hasAction ? '"heading actions actions" "search search search" "dates dates refresh"' : '"heading heading heading" "search search search" "dates dates refresh"',
            sm: '"heading heading dates refresh" "search actions actions actions"'
        }
    }}>
        <Box sx={{gridArea: 'heading', display: 'flex', alignItems: 'center', gap: {xs: 1, sm: 1.5}, minWidth: 0}}>
            <Box sx={{
                width: {xs: 32, sm: 40}, height: {xs: 32, sm: 40}, display: 'grid', placeItems: 'center', borderRadius: '10px', flexShrink: 0,
                bgcolor: theme => alpha(theme.palette.primary.main, 0.1), color: 'primary.main'
            }}>
                <Icon sx={{fontSize: 21}}/>
            </Box>
            <Box sx={{minWidth: 0}}>
                <Typography
                    component="h1"
                    sx={{fontSize: {xs: 18, sm: 20}, fontWeight: 700, lineHeight: 1.3}}>
                    {view.title}
                </Typography>
                <Typography
                    sx={{display: {xs: 'none', sm: 'block'}, fontSize: 12, color: 'text.secondary', mt: 0.5, whiteSpace: 'normal'}}>
                    {view.description}
                </Typography>
            </Box>
        </Box>
        <Box sx={{gridArea: 'dates', minWidth: 0, overflowX: 'auto'}}>
            <AirbnbDateRangePicker from={from} to={to} dateLabel={dateLabel} onChangeDate={updateDateRange}/>
        </Box>
        <Tooltip title="Refresh logs">
            <IconButton
                color="inherit"
                onClick={onClickRefresh}
                aria-label="Refresh logs"
                sx={{
                    gridArea: 'refresh',
                    justifySelf: 'end',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '8px',
                    backgroundColor: 'background.darkBlue'
                }}
            >
                <Refresh fontSize="small"/>
            </IconButton>
        </Tooltip>

        <TextField
            autoFocus={!mobile && searchTerm !== ''}
            value={search} size="small"
            placeholder="Search by Click ID"
            onChange={event => {
                setSearch(event.target.value);
                updateSearch(event.target.value);
            }}
            sx={{
                gridArea: 'search', width: '100%', minWidth: 0, m: 0,
                '& .MuiOutlinedInput-root': {height: {xs: 38, sm: 38}, borderRadius: '8px', bgcolor: 'background.input'},
                '& .MuiOutlinedInput-input': {
                    bgcolor: 'transparent', color: 'text.primary', fontSize: {xs: 16, sm: 13}, py: 1,
                    '&::placeholder': {color: 'text.secondary', opacity: 1}
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: 'primary.main'}
            }}
            slotProps={{
                htmlInput: {'aria-label': 'Search by Click ID'},
                input: {
                    startAdornment: <InputAdornment position="start">
                        <Search sx={{fontSize: 18, color: 'text.secondary'}}/>
                    </InputAdornment>,
                    endAdornment: search ?
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                aria-label="Clear search"
                                onClick={() => {
                                    setSearch('');
                                    updateSearch.cancel();
                                    onSearch.current('');
                                }}
                                sx={{width: {xs: 36, sm: 24}, height: {xs: 36, sm: 24}, p: 0.5}}>
                                <Clear sx={{fontSize: 16}}/>
                            </IconButton>
                        </InputAdornment> : undefined
                }
            }}
        />

        {hasAction && <Box
            sx={{gridArea: 'actions', justifySelf: {xs: 'end', sm: 'start'}, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, minWidth: 0}}>
            {view.action && <Tooltip title={selectedRow ? view.action : 'Select one row to continue'}>
                <span>
                    {mobile ? <IconButton
                        color="primary"
                        disabled={!selectedRow}
                        onClick={() => history.push({pathname: view.route + selectedRow.clickId, state: {background: location}})}
                        aria-label={view.action}
                        sx={{
                            width: 36,
                            height: 36,
                            border: 1,
                            borderColor: 'primary.main',
                            borderRadius: '8px',
                            '&.Mui-disabled': {borderColor: 'divider'}
                        }}
                    >
                        <ActionIcon fontSize="small"/>
                    </IconButton> : <Button
                        variant="outlined"
                        disabled={!selectedRow}
                        startIcon={<ActionIcon sx={{fontSize: {xs: 16, sm: 18}}}/>}
                        onClick={() => history.push({pathname: view.route + selectedRow.clickId, state: {background: location}})}
                        aria-label={view.action}
                        sx={{
                            height: 38,
                            px: {xs: 1, sm: 1.5},
                            borderRadius: '8px',
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            '&.Mui-disabled': {borderColor: 'divider'}
                        }}
                    >
                        {view.action}
                    </Button>}
                </span>
            </Tooltip>}
            {children}
        </Box>}
    </Box>;
}

const mapStateToProps = (state) => {
    return {
        ddToolbarState: state.ddToolbar
    }
};

const actionCreators = {
    updateDateRange: ddToolbarActions.updateDateRange
};

export default connect(mapStateToProps, actionCreators)(LogToolbar);
